import { useState, type MouseEvent } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
} from 'src/components/ui/dropdown-menu';
import { TaskRowMenuItems, type TaskRowMenuTask } from './TaskRowMenu';

/**
 * Right-click menu for task rows: `openAt` goes on the row's context-menu
 * handler and `menu` renders once beside the table. The menu has no trigger
 * element, so it anchors to a zero-size virtual rect at the cursor.
 */
export function useTaskRowContextMenu<TTask extends TaskRowMenuTask>(
  onSnooze?: (task: TTask) => void,
  onDelete?: (task: TTask) => void,
) {
  const [target, setTarget] = useState<{
    x: number;
    y: number;
    task: TTask;
  } | null>(null);

  const anchor = target
    ? {
        getBoundingClientRect: () => new DOMRect(target.x, target.y, 0, 0),
      }
    : undefined;

  function openAt(event: MouseEvent, task: TTask) {
    event.preventDefault();
    setTarget({ x: event.clientX, y: event.clientY, task });
  }

  const menu = (
    <DropdownMenu
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) setTarget(null);
      }}
    >
      <DropdownMenuContent
        anchor={anchor}
        side="bottom"
        align="start"
        sideOffset={0}
      >
        {target && (
          <TaskRowMenuItems
            task={target.task}
            onSnooze={onSnooze ? () => onSnooze(target.task) : undefined}
            onDelete={onDelete ? () => onDelete(target.task) : undefined}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return { openAt, menu };
}
